/* eslint-disable camelcase */
import { setUser } from '@sentry/browser'
import Cookies from 'js-cookie'
import { flow } from 'lodash'
import moment from 'moment'

import { Modal as ModalV5 } from 'antd'
import Modal from 'antd-v3/es/modal'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'
import webSocket from '@emmacare/common/utils/socket'

import { setUnreadMessagesCount } from 'src/store/modules/actionCreators'
import {
  getCliniciansList,
  setWaitingRoomCount
} from 'src/store/modules/actions/telehealth'
import {
  setCompanyGamificationVisibility,
  setRedemptionBadgeCount
} from 'src/store/modules/admin/gamification'
import { setNewAlertsCount } from 'src/store/modules/alerts'
import { setActivityTimerModalInitialValues } from 'src/store/modules/common/actionCreators'
import {
  checkActiveActivityTimer,
  fetchActiveActivityTimer,
  fetchCareTeams
} from 'src/store/modules/common/thunks'
import { clearCompanyAdministrating } from 'src/store/modules/companiesAdministrating'
import {
  getCaseManagers,
  getCompanyDiseases,
  getCompanyReferences,
  getCompanyStructure
} from 'src/store/modules/company'
import { setRecentlyViewedPatients } from 'src/store/modules/patients'
import {
  getAuthToken,
  setAuthenticationState,
  setIsUrgentCareUser,
  setReloadPage,
  setRememberPasswordExpiration,
  setRibbonTimestamp,
  setUserActivitiesCounters,
  setUserDataLoadedState,
  setUserProfile,
  setUserRoles
} from 'src/store/modules/user/actionCreators'

import { queryClient } from 'src/components/App'
import {
  ACKNOWLEDGE_COMPANY_RIBBON,
  AUTH_SET_PASSWORD,
  COMPANY_RIBBONS,
  DETAILED_QUERY_PARAM,
  FETCHING_USER_DATA_ERROR_MESSAGE,
  GET_ACTIVITY_COUNTER,
  ROOT_ADMIN_REFERENCE_TYPES,
  USER_GRID_SETTINGS,
  USER_TYPE_ADMIN,
  USER_TYPE_DEFAULT,
  VERSION_URL
} from 'src/constants'
import api from 'src/services/@core/API'

const setAuthTokens = (accessToken, refreshToken) => {
  const cookies = Cookies

  cookies.set('access_token', accessToken)
  cookies.set('refresh_token', refreshToken)
}

const removeAuthTokens = () => {
  const cookies = Cookies

  cookies.remove('access_token')
  cookies.remove('refresh_token')
}

const getAuthTokens = (credentials) => async (dispatch) => {
  const payload = { grant_type: 'password', ...credentials }

  try {
    const cookies = Cookies
    const response = await api.getAuthTokens(payload)
    const {
      user_type,
      access_token,
      refresh_token,
      gamification_enabled,
      remember_password_expiration,
      mfa_enabled,
      isDeliveryRootAdmin = false
    } = response

    dispatch(getAuthToken({ mfa_enabled: response.mfa_enabled, credentials }))

    if (mfa_enabled) {
      return response
    }

    await dispatch(setRememberPasswordExpiration(remember_password_expiration))

    setAuthTokens(access_token, refresh_token)
    cookies.set('gamification_enabled', gamification_enabled)
    cookies.set('isDeliveryRootAdmin', +isDeliveryRootAdmin)
    cookies.set('user_type', user_type)

    dispatch(setCompanyGamificationVisibility(gamification_enabled))
  } catch (err) {
    dispatch(getAuthToken({ mfa_enabled: err.mfa_enabled, credentials }))
    return err
  }
}

export const refreshAuthTokens = (refreshToken) => async () => {
  const payload = {
    grant_type: 'refresh_token',
    refresh_token: refreshToken
  }

  const { access_token, refresh_token } = await api.refreshAuthTokens(payload)

  setAuthTokens(access_token, refresh_token)
}

export const getUserProfile = () => async (dispatch) => {
  try {
    const profile = await api.getUserProfile()

    setUser({ email: profile?.email })

    const isDeliveryRootAdmin = Boolean(+Cookies.get('isDeliveryRootAdmin'))
    const gamificationEnabled = Cookies.get('gamification_enabled')

    const profileUuid = profile?.uuid || ''

    const roles = profile?.roles || {}
    const userRole = Object.keys(roles)?.[0] || ''
    const isUrgentCareClinician = profile?.isUrgentCareClinician || false
    const companyDivisionUuid = profile?.companyDivision?.uuid || ''
    const recentlyViewedPatients = JSON.parse(
      localStorage.getItem('recentlyViewedPatients')
    )

    await webSocket.connect(profileUuid)

    dispatch(setUserRoles(roles))
    dispatch(setRecentlyViewedPatients(recentlyViewedPatients))
    dispatch(setCompanyGamificationVisibility(gamificationEnabled))
    dispatch(setUserProfile(profile))

    if (!isDeliveryRootAdmin) {
      dispatch(getCliniciansList())
      dispatch(fetchActiveActivityTimer())
    }

    return {
      userRole,
      isUrgentCareClinician,
      companyDivisionUuid,
      companyType: profile?.companyType
    }
  } catch (_) {
    Promise.reject(new Error(FETCHING_USER_DATA_ERROR_MESSAGE))
  }
}

export const updateUserProfile = () => async (dispatch) => {
  try {
    const profile = await api.getUserProfile()

    dispatch(setUserProfile(profile))
  } catch (_) {
    Promise.reject(new Error(FETCHING_USER_DATA_ERROR_MESSAGE))
  }
}

export const fetchCompanyRibbons = () => async (dispatch) => {
  const res = await httpClient.get({
    pathname: COMPANY_RIBBONS
  })

  localStorage.setItem('companyRibbons', JSON.stringify(res))
  dispatch(setRibbonTimestamp(moment().unix()))
}

export const acknowledgeCompanyRibbon = (uuid) => async (dispatch) => {
  await httpClient.post({
    pathname: ACKNOWLEDGE_COMPANY_RIBBON,
    params: { uuid }
  })

  dispatch(fetchCompanyRibbons())
}

export const checkAppVersion = async () => {
  try {
    const { js = '', css = '' } = await httpClient.request({
      method: 'get',
      url: {
        pathname: VERSION_URL,
        query: { env: process.env.ENVIRONMENT_TYPE }
      }
    })

    let shouldReload = flow(
      (scripts) => Array.prototype.slice.apply(scripts),
      (scripts) =>
        scripts?.find(({ src = '' }) => src.indexOf('/main.') !== -1)?.src ||
        '',
      (src) => src.indexOf(js) === -1
    )(document.querySelectorAll('script'))

    if (!shouldReload) {
      shouldReload = flow(
        (links) => Array.prototype.slice.apply(links),
        (links) =>
          links?.find(
            ({ href = '', rel = '' }) =>
              rel === 'stylesheet' && href.indexOf('/main.') !== -1
          )?.href || '',
        (href) => href.indexOf(css) === -1
      )(document.querySelectorAll('link'))
    }

    return shouldReload
  } catch (err) {
    catchError(err)

    return false
  }
}

export const fetchRequiredAppData = () => async (dispatch) => {
  try {
    const { isUrgentCareClinician, companyDivisionUuid } = await dispatch(
      getUserProfile()
    )

    const isDeliveryRootAdmin = Boolean(+Cookies?.get('isDeliveryRootAdmin'))

    const userType = +Cookies?.get('user_type') || USER_TYPE_DEFAULT
    const isRootAdmin = userType === USER_TYPE_ADMIN

    if (!isDeliveryRootAdmin) {
      await Promise.all(
        [
          dispatch(
            getCompanyReferences({
              'filter[enabled]': 'eq:1',
              ...(isRootAdmin && {
                'filter[type]': `in:${ROOT_ADMIN_REFERENCE_TYPES.join(',')}`
              }),
              sort: 'refValue:asc'
            })
          ),
          dispatch(
            getCaseManagers({
              query: {
                ...DETAILED_QUERY_PARAM,
                sort: 'fullName:asc',
                'per-page': 10000
              }
            })
          ),
          dispatch(getCompanyDiseases({ sort: 'name:asc', 'per-page': 1000 }))
        ].concat(
          userType === USER_TYPE_DEFAULT
            ? [
                dispatch(getCompanyStructure({ sort: 'name:asc' })),
                dispatch(
                  fetchCareTeams({
                    query: {
                      'filter[division]': `in:${companyDivisionUuid}`,
                      'per-page': 10000,
                      sort: 'name:asc'
                    }
                  })
                )
              ]
            : []
        )
      )
    }

    dispatch(setIsUrgentCareUser(isUrgentCareClinician))
    dispatch(setUserDataLoadedState(true))
  } catch (err) {
    return err
  }
}

export const login = (credentials) => async (dispatch) => {
  try {
    const res = await dispatch(getAuthTokens(credentials))
    const isRootAdmin = +Cookies.get('user_type') === USER_TYPE_ADMIN

    if (res) {
      const { error_description: message, mfa_enabled } = res
      if (mfa_enabled || res.status === 429) return res

      catchError({ message: message || res.message, status: res?.status })

      return res
    }

    const shouldReload = await checkAppVersion()

    if (shouldReload) {
      await dispatch(setReloadPage(shouldReload))

      window.location.reload()
    }

    await dispatch(fetchRequiredAppData())

    if (!isRootAdmin) {
      dispatch(fetchCompanyRibbons())
      dispatch(checkActiveActivityTimer())
      dispatch(setRecentlyViewedPatients([]))

      localStorage.setItem('recentlyViewedPatients', JSON.stringify([]))
      localStorage.setItem('recentlyViewedReferrals', JSON.stringify([]))
      localStorage.setItem('activeActionsShown', 'false')

      sessionStorage.removeItem('appointmentsQueue')
    }

    await dispatch(
      setAuthenticationState({
        isAuthenticated: true,
        isManuallyLoggedOut: false
      })
    )

    dispatch(setReloadPage())
  } catch (err) {
    return err
  }
}

export const logout = () => async (dispatch) => {
  const cookies = Cookies

  localStorage.removeItem('actionHubPredefinedFilter')
  localStorage.removeItem('activeActionsShown')
  localStorage.removeItem('companyRibbons')
  localStorage.removeItem('recentlyViewedPatients')
  localStorage.removeItem('recentlyViewedReferrals')
  localStorage.removeItem('referralDashboardFilters')
  localStorage.removeItem('urgentCareFiltersHidden')
  localStorage.removeItem('waitingRoomFilter')
  localStorage.removeItem('appointmentsFilters')
  localStorage.removeItem('clinicianSchedulePreFilter')
  localStorage.removeItem('billingDetailPartialPersistentFilter')
  localStorage.removeItem('billingSummaryPartialPersistentFilter')
  localStorage.removeItem('programsMonitoringPartialPersistentFilter')
  localStorage.removeItem('predefinedRPMFilters')
  localStorage.removeItem('riskStratificationFilter')
  localStorage.removeItem('predefinedInitialVisitFilter')
  localStorage.removeItem('predefinedRevisitFilter')

  sessionStorage.removeItem('appointmentsQueue')

  cookies.remove('user_type')
  cookies.remove('gamification_enabled')
  cookies.remove('isDeliveryRootAdmin')

  dispatch(
    setAuthenticationState({
      isAuthenticated: false,
      isManuallyLoggedOut: true
    })
  )
  dispatch(setUserProfile({}))
  dispatch(clearCompanyAdministrating())
  removeAuthTokens()

  setUser(null)

  webSocket.disconnect()
  Modal.destroyAll()
  ModalV5.destroyAll()
  queryClient.clear()

  for (let i = 1; i < 5000; i++) {
    clearInterval(i)
  }

  try {
    await api.logout()
    unsetCounters(dispatch)
    dispatch(setActivityTimerModalInitialValues({}))
  } catch (err) {
    return err
  }
}

export const unsetCounters = (dispatch) => {
  dispatch(setRedemptionBadgeCount(0))
  dispatch(setWaitingRoomCount({ scheduled: 0, waiting: 0 }))
  dispatch(setNewAlertsCount(0))
  dispatch(setUnreadMessagesCount(0))
}

export const sendPasswordRestorationEmail = (email) => () =>
  api.forgotPassword({ email })

export const setPassword = (payload) => async () => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: AUTH_SET_PASSWORD
      },
      payload
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const getUserActivitiesCounters =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { totalAmount: userAmount, totalMin: userMin } =
        await httpClient.request({
          method: 'get',
          url: {
            pathname: GET_ACTIVITY_COUNTER,
            query
          }
        })

      dispatch(
        setUserActivitiesCounters({
          userAmount,
          userMin
        })
      )
    } catch (err) {
      catchError(err)
    }
  }

export const updateUserGridSettings =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      const gridSettings = await httpClient.request({
        method: 'patch',
        url: {
          pathname: USER_GRID_SETTINGS,
          params
        },
        payload
      })
      dispatch(setUserProfile({ gridSettings }))

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }
